/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 2 (dev-sync) B3 — _persistVfdAccessory mirror (fan.selector.js
 * 8688-8745). Called from the React doSelect SELECT path ONLY (js:6268-6273 —
 * never on Rate), fire-and-forget like the legacy call site (the dialog hides
 * without awaiting).
 *
 * Flow parity (analysis-backbone-persist.md §2):
 *  - CREATE (has_vfd && payload && no existing id): POST /ahu/vfds/ →
 *    PATCH /ahu/vfds/<id> with the SAME body → PATCH /ahu/positions/<id>
 *    (position id == vfd id backend-side), then validateSectionAccessories +
 *    accessory re-render (js:8716-8730).
 *  - UPDATE (existing id): Backbone issues NO REST call here — vfdAccessory
 *    .set(payload) leans on Backbone-model AUTOSAVE (the two PATCHes are
 *    commented out in the source, js:8705-8715). React has no model autosave
 *    on this path, so the pragmatic mirror IS those two PATCHes — the same
 *    net writes the autosave produces. DOCUMENTED DEVIATION.
 *  - DELETE on uncheck (existing id, no has_vfd/vfd): DELETE /ahu/vfds/<id> +
 *    remove_accessory_from_canvas + validateSectionAccessories, NO
 *    confirmation dialog (js:8732-8741).
 *
 * React-specific tail: Backbone's accessories tab + canvas learn about the
 * new/changed row via websocket-driven view renders (_rerenderVfdAccessory
 * js:8747-8760 + the new-accessory websocket flow). The React equivalents are
 * a getSectionAccessories refetch (the same call createAccessoryAny makes,
 * boardActions.ts:257-261) and an explicit draw:accessory:on:canvas emit with
 * the FINAL serializer row.
 */
import type { AppDispatch } from 'app/store';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { validateSectionAccessories } from '../../validation/validationController';
import { buildAccessoryBootDraw } from '../../three/bootDrawFeeds';
import { fanVfdApi, omitNulls, type VfdPayload } from './fanVfdApi';

/**
 * One-shot query read: initiate → unwrap → RELEASE the subscription. A held
 * subscription refetches on every later tag invalidation — for the delete
 * path that means recurring GETs of a dead id (vfds/<id> → 400,
 * positions/<id> → 404 noise on every subsequent AhuVfds/AhuAccessories
 * invalidation). Errors resolve to null (caller-side fallbacks).
 */
async function fetchOnce<T>(sub: {
  unwrap(): Promise<T>;
  unsubscribe(): void;
}): Promise<T | null> {
  try {
    return await sub.unwrap();
  } catch {
    return null;
  } finally {
    sub.unsubscribe();
  }
}

export interface PersistVfdArgs {
  /** isVfdValid() ? buildVfdPayload(...) : null — the legacy call arg (js:6271-6272). */
  payload: VfdPayload | null;
  /** !!state.has_vfd && !!self.vfd (js:8704). */
  hasVfd: boolean;
  /** The persisted AHUAccessoryVFD id (legacy self.vfdAccessory?.id). */
  existingId: number | null;
  /** The fan's own section (legacy self.section?.id) — the delete-validation fallback. */
  fanSectionId: number | null;
}

/**
 * Fetch the FINAL serializer row + position and emit the canvas draw. The
 * position PATCH re-runs AHUAccessoryVFD.save() via position_viewset.py:57-66
 * and the serializer's computed bank fields (vfd_bank_positions, width_calc,
 * distance_to_right_wall, …) depend on the position — so re-GET after all
 * writes instead of trusting the earlier PATCH echo. (Backbone gets the fresh
 * row over the websocket channel instead.) buildAccessoryBootDraw applies the
 * exact drawOnCanvas gates (master_vfd_id / plane / width / height /
 * int-wall resolution) — a gated-out row simply doesn't draw, same as legacy.
 */
async function redrawVfdOnCanvas(dispatch: AppDispatch, id: number): Promise<void> {
  const [detail, position] = await Promise.all([
    fetchOnce(
      dispatch(
        ahuBoardApi.endpoints.getAccessoryDetail.initiate(
          { plural: 'vfds', id },
          { forceRefetch: true }
        )
      )
    ),
    fetchOnce(
      dispatch(ahuBoardApi.endpoints.getAccessoryPosition.initiate(id, { forceRefetch: true }))
    ),
  ]);
  if (!detail || !position) return;
  const sectionId = Number((detail as Record<string, any>).section_id);
  // plane>6 = internal-wall mount: the draw builder needs the wall rows to
  // resolve the parent (cache hit normally — both lists are booted at load).
  const planeId = Number((position as Record<string, any>).mounting_plane_id) || 0;
  let intWalls: any[] = [];
  if (planeId > 6 && Number.isFinite(sectionId)) {
    const [v, h] = await Promise.all([
      fetchOnce(dispatch(ahuBoardApi.endpoints.getSectionVerticalIntWalls.initiate(sectionId))),
      fetchOnce(dispatch(ahuBoardApi.endpoints.getSectionHorizontalIntWalls.initiate(sectionId))),
    ]);
    intWalls = [...(v ?? []), ...(h ?? [])];
  }
  const draw = buildAccessoryBootDraw(
    'vfd',
    { row: detail as any, detail: detail as any, position: position as any },
    intWalls
  );
  if (!draw) return;
  // isAlreadyRendered=false is safe for create AND update — drawAccessory
  // destroys a registered shape before drawing (canvas3dGeometry.ts:5050).
  ahuBoardBus.emit(
    'draw:accessory:on:canvas',
    draw.accessory,
    draw.label,
    { id: draw.sectionId },
    draw.mounting,
    false,
    draw.viewport,
    draw.int_wall
  );
}

/**
 * Returns the persisted VFD id (created or existing) so the caller can retain
 * it — a second Select in the same dialog session must UPDATE, not re-create
 * — or null after a delete/no-op.
 */
export async function persistVfdAccessory(
  dispatch: AppDispatch,
  args: PersistVfdArgs
): Promise<number | null> {
  const { payload, hasVfd, existingId, fanSectionId } = args;
  try {
    if (hasVfd && payload) {
      const vfdBody = omitNulls(payload.vfd as unknown as Record<string, unknown>);
      const posBody = omitNulls(payload.position as unknown as Record<string, unknown>);
      let id: number;
      if (existingId) {
        // UPDATE — the autosave-equivalent PATCH pair (see the header note).
        id = existingId;
        await dispatch(fanVfdApi.endpoints.patchAhuVfd.initiate({ id, data: vfdBody })).unwrap();
        await dispatch(
          fanVfdApi.endpoints.patchAhuVfdPosition.initiate({ id, data: posBody })
        ).unwrap();
      } else {
        // CREATE (js:8716-8730): POST → PATCH vfd with the SAME body →
        // PATCH position (position id == vfd id).
        const created = await dispatch(fanVfdApi.endpoints.postAhuVfd.initiate(vfdBody)).unwrap();
        id = Number(created.id);
        await dispatch(fanVfdApi.endpoints.patchAhuVfd.initiate({ id, data: vfdBody })).unwrap();
        await dispatch(
          fanVfdApi.endpoints.patchAhuVfdPosition.initiate({ id, data: posBody })
        ).unwrap();
        const sectionId = Number(payload.vfd.section_id);
        if (Number.isFinite(sectionId)) {
          // ahu.validateSectionAccessories(payload.vfd.section_id) (js:8730).
          void dispatch(validateSectionAccessories(sectionId));
          // Accessories-tab row without a reload (Backbone: websocket-driven).
          // fetchOnce releases the temp subscription; mounted SectionAccessories
          // subscribers keep the refreshed cache entry alive.
          void fetchOnce(
            dispatch(
              ahuBoardApi.endpoints.getSectionAccessories.initiate(sectionId, {
                forceRefetch: true,
              })
            )
          );
        }
      }
      await redrawVfdOnCanvas(dispatch, id);
      return id;
    } else if (existingId) {
      // DELETE on uncheck (js:8732-8741) — no confirmation dialog (parity).
      // Backbone reads the PERSISTED section off self.vfdAccessory; mirror by
      // fetching the row (cache-served when the VFD tab loaded it) before it
      // is gone. fetchOnce is REQUIRED here — a held subscription on the
      // soon-dead id would refetch (→ 400) on every later invalidation.
      const row = await fetchOnce(
        dispatch(
          ahuBoardApi.endpoints.getAccessoryDetail.initiate({ plural: 'vfds', id: existingId })
        )
      );
      const persistedSectionId =
        Number((row as Record<string, any> | null)?.section_id) || fanSectionId || null;
      await dispatch(fanVfdApi.endpoints.deleteAhuVfd.initiate(existingId)).unwrap();
      ahuBoardBus.emit('remove_accessory_from_canvas', existingId);
      // …and the record itself is gone, which the canvas channel above does NOT
      // imply (the draw gate hides live rows through it too).
      ahuBoardBus.emit('accessory:deleted', existingId);
      if (persistedSectionId != null) {
        void dispatch(validateSectionAccessories(persistedSectionId));
        void fetchOnce(
          dispatch(
            ahuBoardApi.endpoints.getSectionAccessories.initiate(persistedSectionId, {
              forceRefetch: true,
            })
          )
        );
      }
      if (fanSectionId != null && fanSectionId !== persistedSectionId) {
        void dispatch(validateSectionAccessories(fanSectionId));
      }
      return null;
    }
    return existingId ?? null;
  } catch (err) {
    console.error('VFD persistence failed', err); // js:8742-8744
    return existingId ?? null;
  }
}
